import { Form, Checkbox, Col, Row } from 'antd';
import FloatingFormItem from '../../FloatingFormItem';
import InputDecimal from '../../InputDecimal';
import SectionCard from '../../SectionCard';

interface Props {
  prefixPath?: string[];
}

/**
 * Tributação Monofásica (NT 2025.002 v1.40 — grupo UB84 gIBSCBSMono).
 * Layout v1.40 (produção 03/08/2026): gMonoPadrao / gMonoReten / gMonoRet /
 * gMonoDif. A separação Ad Rem × Ad Valorem existe apenas na v1.50
 * (produção 03/11/2026) e está comentada no fim do arquivo.
 */
export default function SinglePhaseFormFields({
  prefixPath = ['itemTaxes', 'ibsCbs'],
}: Props) {
  const form = Form.useFormInstance();
  const enableMono = Form.useWatch('enableMono', form);

  return (
    <>
      <div>
        <Form.Item name="enableMono" valuePropName="checked">
          <Checkbox style={{ fontWeight: 600 }}>
            Exibir Grupo Monofásico
          </Checkbox>
        </Form.Item>
      </div>

      <div style={{ display: enableMono ? 'block' : 'none' }}>
        {/* ===== UB84a gMonoPadrao — Monofásico Padrão ===== */}
        <SectionCard
          visible
          style={{ marginBottom: 24 }}
          title="Monofásico - Padrão"
          subtitle="v = adRem × quantidade"
        >
          <Row gutter={[16, 16]}>
            {/* UB85 qBCMono */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Quantidade Tributada"
                name={[...prefixPath, 'monoQuantity']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB86 adRemIBS */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Alíquota Ad Rem IBS"
                name={[...prefixPath, 'monoAdRemIbsAliquotPercentage']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB87 adRemCBS */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Alíquota Ad Rem CBS"
                name={[...prefixPath, 'monoAdRemCbsAliquotPercentage']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB88 vIBSMono */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Valor do IBS"
                name={[...prefixPath, 'monoIbsValue']}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            {/* UB89 vCBSMono */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Valor do CBS"
                name={[...prefixPath, 'monoCbsValue']}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </SectionCard>

        {/* ===== UB90 gMonoReten — Monofásico Sujeito à Retenção ===== */}
        <SectionCard
          visible
          style={{ marginBottom: 24 }}
          title="Monofásico - Sujeito à Retenção"
        >
          <Row gutter={[16, 16]}>
            {/* UB91 qBCMonoReten */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Quantidade Tributada"
                name={[...prefixPath, 'monoRetentionQuantity']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB92 adRemIBSReten */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Alíquota Ad Rem IBS"
                name={[...prefixPath, 'monoRetentionAdRemIbsAliquotPercentage']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB93 vIBSMonoReten */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Valor do IBS"
                name={[...prefixPath, 'monoRetentionIbsValue']}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            {/* UB93a adRemCBSReten */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Alíquota Ad Rem CBS"
                name={[...prefixPath, 'monoRetentionAdRemCbsAliquotPercentage']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB93b vCBSMonoReten */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Valor do CBS"
                name={[...prefixPath, 'monoRetentionCbsValue']}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </SectionCard>

        {/* ===== UB94 gMonoRet — Monofásico Retida Anteriormente ===== */}
        <SectionCard
          visible
          style={{ marginBottom: 24 }}
          title="Monofásico - Retida Anteriormente"
        >
          <Row gutter={[16, 16]}>
            {/* UB95 qBCMonoRet */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Quantidade Tributada"
                name={[...prefixPath, 'monoRetainedQuantity']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB96 adRemIBSRet */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Alíquota Ad Rem IBS"
                name={[...prefixPath, 'monoRetainedAdRemIbsAliquotPercentage']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB97 vIBSMonoRet */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Valor do IBS"
                name={[...prefixPath, 'monoRetainedIbsValue']}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            {/* UB98 adRemCBSRet */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Alíquota Ad Rem CBS"
                name={[...prefixPath, 'monoRetainedAdRemCbsAliquotPercentage']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB98a vCBSMonoRet */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Valor do CBS"
                name={[...prefixPath, 'monoRetainedCbsValue']}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </SectionCard>

        {/* ===== UB99 gMonoDif — Diferimento da Tributação Monofásica ===== */}
        <SectionCard
          visible
          style={{ marginBottom: 24 }}
          title="Monofásico - Diferimento"
        >
          <Row gutter={[16, 16]}>
            {/* UB100 pDifIBS */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Imposto IBS (%)"
                name={[...prefixPath, 'monoDeferralIbsPercentage']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB101 vIBSMonoDif */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Valor IBS"
                name={[...prefixPath, 'monoDeferralIbsValue']}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            {/* UB102 pDifCBS */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Imposto CBS (%)"
                name={[...prefixPath, 'monoDeferralCbsPercentage']}
              >
                <InputDecimal fractions={4} />
              </FloatingFormItem>
            </Col>

            {/* UB103 vCBSMonoDif */}
            <Col span={24} sm={12} md={6}>
              <FloatingFormItem
                label="Valor CBS"
                name={[...prefixPath, 'monoDeferralCbsValue']}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>
          </Row>
        </SectionCard>

        <h2>Totais Monofásico</h2>

        <Row gutter={[16, 16]}>
          <Col span={24} sm={12} md={6}>
            <FloatingFormItem
              label="Total IBS (auto)"
              name={[...prefixPath, 'monoItemIbsTotal']}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={6}>
            <FloatingFormItem
              label="Total CBS (auto)"
              name={[...prefixPath, 'monoItemCbsTotal']}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>

        {/* ================================================================
            NT v1.50 (produção 03/11/2026) — NÃO usar na v1.40.
            A v1.50 reformula o monofásico separando Ad Rem × Ad Valorem:
            - IBS Ad Valorem (UB90..UB93 v1.50): ibsMonoAdValoremVBcMono,
              ibsMonoAdValoremPAliqMonoUf, ibsMonoAdValoremVIbsMonoUf,
              ibsMonoAdValoremPAliqMonoMun, ibsMonoAdValoremVIbsMonoMun,
              ibsMonoAdValoremVBcMonoReten, ibsMonoAdValoremPAliqMonoReten,
              ibsMonoAdValoremVIbsMonoReten, ibsMonoAdValoremVIbsMonoRet
            - CBS Ad Rem qBC (UB96a/UB97a v1.50): cbsMonoAdRemQBcMono,
              cbsMonoAdRemQBcMonoReten
            - CBS Ad Valorem (UB100..UB103 v1.50): cbsMonoAdValoremVBcMono,
              cbsMonoAdValoremPAliqMonoCbs, cbsMonoAdValoremVCbsMono,
              cbsMonoAdValoremVBcMonoReten, cbsMonoAdValoremPAliqMonoReten,
              cbsMonoAdValoremVCbsMonoReten, cbsMonoAdValoremVCbsMonoRet
            Reativar as seções (e os campos no data-layer) quando a v1.50
            entrar em vigor.
            ================================================================ */}
      </div>
    </>
  );
}
